import React, { useCallback, useEffect, useState } from 'react';
import { SalvButton, SalvLabel, SalvProgressStepper, Step } from '@salvapp/ui-components';
import { useScreenableFieldSetupNavigation } from './helpers/useScreenableFieldSetupNavigation';
import { ScreenableDataStep } from './steps/ScreenableDataStep';
import './ScreenableFieldSetup.scss';
import {
  ScreenableFieldSetupContext,
  useScreenableFieldSetupContextManager,
} from './helpers/useScreenableFieldSetupContextManager';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { IncludedSegmentsStep } from './steps/IncludedSegmentsStep';
import { ExcludedSegmentsStep } from './steps/ExcludedSegmentsStep';
import { GoodlistingStep } from './steps/GoodlistingStep';
import { InternalApiEntityType, InternalApiScreeningFlow, instanceOfInternalApiEntityType } from '../../api-internal';
import { useLocation, useNavigate } from 'react-router';
import { MatchingTypeStep } from './steps/MatchingTypeStep';
import { ListSelectionStep } from './steps/ListSelectionStep';
import { SummaryStep } from './steps/SummaryStep';
import GroupSelectionStep from './steps/GroupSelectionStep';
import { getFlow } from './helper';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { useCreateScreeningFlow, useUpdateScreeningFlow } from '../configure/new/screeningFlowActions';
import { ApiSanctionType } from '../../api';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { getScreeningFlowPath, InternalNavigation } from '../../navigation/helper';
import { SearchParamKeys } from '../../common/searchParamKeys';
import { useCreateDraftForFlow, useCreateNewFlowDraft, useUpdateFlowDraft } from './draft/screeningFlowDraftActions';
import { SaveAsDraftModal } from './draft/SaveAsDraftModal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';
import { showFeedback } from '../../feedback/feedbackReducer';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';

export const ScreenableFieldSetup = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const flow: InternalApiScreeningFlow | undefined = location.state?.flow ?? undefined;
  const entityTypeParam = searchParams.get(SearchParamKeys.entityType);
  const entityType =
    flow?.entityType ??
    (instanceOfInternalApiEntityType(entityTypeParam) ? (entityTypeParam as InternalApiEntityType) : undefined);

  if (!entityType) {
    return <DataLoadingErrorFallback message='Could not determine entity type for the screening flow setup' />;
  }

  return <ScreenableFieldSetupWizard entityType={entityType} flow={flow} />;
};

interface WizardProps {
  entityType: InternalApiEntityType;
  flow?: InternalApiScreeningFlow;
}

const ScreenableFieldSetupWizard = ({ entityType, flow }: WizardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDraft = location.state?.isDraft === true;
  const draftId = location.state?.draftId as number | undefined;
  const initialNote = location.state?.note as string | undefined;

  const setupContext = useScreenableFieldSetupContextManager(entityType, flow);
  const isNewConfiguration = !setupContext.flowId;

  const [steps, activeStep, activeStepIndex, progressStepperSteps, nextStep, previousStep, setListCodeStepVisible] =
    useScreenableFieldSetupNavigation(entityType);

  const { mutateAsync: createScreeningFlow } = useCreateScreeningFlow(entityType);
  const { mutateAsync: updateScreeningFlow } = useUpdateScreeningFlow(entityType);
  const { mutateAsync: createFlowDraft } = useCreateDraftForFlow(setupContext.flowId ?? '');
  const { mutateAsync: createNewFlowDraft } = useCreateNewFlowDraft();
  const { mutateAsync: updateFlowDraft } = useUpdateFlowDraft();
  const { features } = useMe();
  const isFlowDraftsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SCREENING_FLOW_DRAFTS, features);
  const [isSaveAsDraftModalOpen, setIsSaveAsDraftModalOpen] = useState(false);

  const ActiveSetupPage = useCallback(() => {
    switch (activeStep.type) {
      case 'SCREENABLE_DATA':
        return <ScreenableDataStep />;
      case 'GOODLIST':
        return <GoodlistingStep />;
      case 'INCLUDED_GROUPS':
        return <IncludedSegmentsStep />;
      case 'EXCLUDED_GROUPS':
        return <ExcludedSegmentsStep />;
      case 'MATCHING_TYPE':
        return <MatchingTypeStep />;
      case 'USABLE_DATA':
        return <ListSelectionStep />;
      case 'SELECTOR_CODE':
        return <GroupSelectionStep />;
      case 'SUMMARY':
        return <SummaryStep />;
    }
  }, [activeStep]);

  const navigateToScreeningFlows = () =>
    navigate({
      pathname: InternalNavigation.screeningFlows,
      search: createSearchParams({ [SearchParamKeys.entityType]: entityType }).toString(),
    });

  const discardSetup = useCallback(() => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure you want to discard the changes?',
        confirmationText: 'Yes',
        cancelText: 'Cancel',
        onConfirm: () => navigateToScreeningFlows(),
      })
    );
  }, []);

  const saveAsDraft = async (note: string | undefined) => {
    setIsSubmitting(true);
    const flow = getFlow(setupContext);
    const body = { entityType, snapshot: flow, note };
    try {
      if (draftId !== undefined) {
        await updateFlowDraft({ draftId, body });
        setIsSaveAsDraftModalOpen(false);
        dispatch(showFeedback({ message: 'Draft updated', success: true }));
        navigate(getScreeningFlowPath(flow.id!));
      } else if (isNewConfiguration) {
        await createNewFlowDraft(body);
        setIsSaveAsDraftModalOpen(false);
        dispatch(showFeedback({ message: 'Draft saved', success: true }));
        navigateToScreeningFlows();
      } else {
        await createFlowDraft(body);
        setIsSaveAsDraftModalOpen(false);
        dispatch(showFeedback({ message: 'Draft saved', success: true }));
        navigate(getScreeningFlowPath(flow.id!));
      }
    } catch {
      dispatch(showFeedback({ message: 'Failed to save draft', success: false }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveScreeningFlow = useCallback(() => {
    setIsSubmitting(true);
    const flow = getFlow(setupContext);
    if (isNewConfiguration) {
      createScreeningFlow(flow)
        .then(() => navigateToScreeningFlows())
        .finally(() => setIsSubmitting(false));
    } else {
      updateScreeningFlow({
        flowId: setupContext.flowId!,
        flow,
      })
        .then(() => navigateToScreeningFlows())
        .finally(() => setIsSubmitting(false));
    }
  }, [setupContext, createScreeningFlow, entityType]);

  useEffect(() => {
    if (setupContext.dowJonesListTypes.includes(ApiSanctionType.SANCTION)) {
      setListCodeStepVisible(true);
    } else {
      setListCodeStepVisible(false);
    }
  }, [setupContext.dowJonesListTypes, setListCodeStepVisible]);

  return (
    <ScreenableFieldSetupContext.Provider value={setupContext}>
      <div className='screenable-field-setup'>
        <div className='setup-content'>
          <div className='setup-title m-x-24 m-t-24 m-b-0'>
            <h2 className='m-b-0'>
              {draftId !== undefined ? 'Edit draft' : isNewConfiguration ? 'New Screening Flow' : 'Edit Screening Flow'}
            </h2>
            {isDraft && isFlowDraftsEnabled && <SalvLabel variant='secondary'>Draft</SalvLabel>}
          </div>
          <ActiveSetupPage />
        </div>
        <div className='setup-sidebar'>
          <div className='m-b-24' style={{ display: 'flex', justifyContent: 'end' }}>
            <SalvButton type='button' variant='muted' label='Discard setup' onClick={discardSetup} />
          </div>
          <SalvProgressStepper steps={progressStepperSteps} activeStep={activeStepIndex + 1} navigable={false}>
            {steps.map((step, index) => (
              <Step key={index} header={step.title} description={step.description} />
            ))}
          </SalvProgressStepper>
          <div className='wizard-nav-buttons'>
            {activeStepIndex !== 0 && (
              <SalvButton
                type='button'
                variant='muted'
                label='Previous'
                onClick={previousStep}
                iconLeft={faChevronLeft}
              />
            )}
            {activeStepIndex !== steps.length - 1 && (
              <SalvButton
                type='button'
                variant='primary'
                label={setupContext.completedSteps.get(activeStep.type) === 'skip' ? 'Skip' : 'Next'}
                onClick={nextStep}
                iconRight={faChevronRight}
                disabled={!setupContext.completedSteps.get(activeStep.type)}
              />
            )}
            {activeStepIndex === steps.length - 1 && !isFlowDraftsEnabled && (
              <SalvButton
                type='button'
                variant='primary'
                label='Save'
                onClick={saveScreeningFlow}
                disabled={isSubmitting || setupContext.completedSteps.size < steps.length - 1}
              />
            )}
            {activeStepIndex === steps.length - 1 && isFlowDraftsEnabled && (
              <SalvButton
                type='button'
                variant='primary'
                label='Save'
                onClick={isDraft ? () => setIsSaveAsDraftModalOpen(true) : saveScreeningFlow}
                disabled={isSubmitting || setupContext.completedSteps.size < steps.length - 1}
              />
            )}
          </div>
        </div>
        {isSaveAsDraftModalOpen && (
          <SaveAsDraftModal
            onClose={() => setIsSaveAsDraftModalOpen(false)}
            onSubmit={saveAsDraft}
            initialNote={initialNote}
          />
        )}
      </div>
    </ScreenableFieldSetupContext.Provider>
  );
};
